import React, { useState } from 'react';
import Glide from '@glidejs/glide';
import '@glidejs/glide/dist/css/glide.core.css';
import imagesloaded from 'imagesloaded';

import { useImperativeHandle, useEffect, useRef, forwardRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import useCheckoutState from '@repo/checkout/hooks/useCheckoutState';
import { checkoutTypes } from '@repo/checkout/enums/checkout-types';

function Resize(Glide, Components, Events) {
  const AUTOHEIGHT = {
    mount() {
      this.calculateHeight();
      Events.on(['mount.after', 'update'], () => {
        this.calculateHeight();
      });

      // Add resize observer to handle dynamic size changes
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.calculateHeight();
        });

        if (Components.Html.root) {
          this.resizeObserver.observe(Components.Html.root);
        }
      }

      // Fallback to window resize for older browsers
      window.addEventListener('resize', () => {
        this.calculateHeight();
      });
    },

    calculateHeight() {
      if (!Components.Html.slides || Components.Html.slides.length === 0) {
        return;
      }

      // Remove transition temporarily during calculation
      Components.Html.track.style.transition = 'none';

      imagesloaded(Components.Html.track, () => {
        this.set();
        // Restore transition after calculation
        requestAnimationFrame(() => {
          Components.Html.track.style.transition = 'height 0.2s ease-out';
        });
      });
    },

    set() {
      if (
        !Components.Html.slides ||
        Components.Html.slides.length === 0 ||
        Glide.index >= Components.Html.slides.length
      ) {
        return;
      }

      const currentSlide = Components.Html.slides[Glide.index];

      if (currentSlide && currentSlide.offsetHeight > 0) {
        const computedHeight = window.getComputedStyle(currentSlide).height;
        requestAnimationFrame(() => {
          Components.Html.track.style.height = computedHeight;
        });
      } else if (Components.Html.slides.length > 0) {
        const firstSlide = Components.Html.slides[0];
        if (firstSlide && firstSlide.offsetHeight > 0) {
          const computedHeight = window.getComputedStyle(firstSlide).height;
          requestAnimationFrame(() => {
            Components.Html.track.style.height = computedHeight;
          });
        }
      }
    },

    unmount() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      window.removeEventListener('resize', this.calculateHeight);
    },
  };

  Events.on(['run'], () => {
    AUTOHEIGHT.set();
  });

  return AUTOHEIGHT;
}

// eslint-disable-next-line react/display-name
const Carousel = forwardRef(({ options, children, count }, ref) => {
  const sliderRef = useRef();
  const [sliderIndex, setSliderIndex] = useState(0);
  const glideInstanceRef = useRef(null);
  const [isClient, setIsClient] = useState(false);
  const { checkoutType } = useCheckoutState();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClient(true);
    }
  }, []);

  useImperativeHandle(ref, () => sliderRef.current);

  const forceHeightRecalculation = () => {
    if (glideInstanceRef.current) {
      const track = sliderRef.current?.querySelector('.glide__track');
      if (track) {
        track.style.transition = 'none';
        glideInstanceRef.current.update();
        requestAnimationFrame(() => {
          track.style.transition = 'height 0.2s ease-out';
        });
      }
    }
  };

  useEffect(() => {
    if (glideInstanceRef.current) {
      glideInstanceRef.current.destroy();
    }

    const slider = new Glide(sliderRef.current, {
      rewind: false,
      gap: 0,
      ...options,
    });

    slider.on('run', () => setSliderIndex(slider.index));
    slider.mount({ Resize });
    glideInstanceRef.current = slider;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        forceHeightRecalculation();
      }
    };

    const handleResize = () => {
      requestAnimationFrame(() => {
        forceHeightRecalculation();
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('resize', handleResize);
      if (glideInstanceRef.current) {
        glideInstanceRef.current.destroy();
        glideInstanceRef.current = null;
      }
    };
  }, [isClient]);

  useEffect(() => {
    if (count > 0) {
      const track = sliderRef.current?.querySelector('.glide__track');
      if (track) {
        track.style.transition = 'none';
        forceHeightRecalculation();

        const timer = setTimeout(() => {
          requestAnimationFrame(() => {
            track.style.transition = '';
            forceHeightRecalculation();
          });
        }, 0);

        return () => clearTimeout(timer);
      }
    }
  }, [children, count]);

  return (
    <div className="glide relative" ref={sliderRef}>
      <div
        className="glide__track"
        data-glide-el="track"
        style={{ transition: 'height 0.2s ease-out' }}
      >
        <ul className="glide__slides">{children}</ul>

        <div className="glide__arrows" data-glide-el="controls">
          <button
            className={clsx(
              'transition-colors bg-white hover:bg-gray-50 px-1 rounded-full h-7 w-7 text-black absolute top-0 bottom-0 mt-auto mb-auto shadow focus:outline-none',
              sliderIndex === 0 ? 'hidden' : '',
              {
                '-left-3.5': checkoutType !== checkoutTypes.EMBED,
                'left-1.5': checkoutType === checkoutTypes.EMBED,
              },
            )}
            data-glide-dir="<"
            name="previous"
          >
            <ChevronLeftIcon />
          </button>

          <button
            className={clsx(
              'transition-colors bg-white hover:bg-gray-50 px-1 rounded-full h-7 w-7 text-black absolute top-0 bottom-0 mt-auto mb-auto shadow focus:outline-none',
              sliderIndex === count - 1 ? 'hidden' : '',
              {
                '-right-3.5': checkoutType !== checkoutTypes.EMBED,
                'right-1.5': checkoutType === checkoutTypes.EMBED,
              },
            )}
            data-glide-dir=">"
            name="next"
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    </div>
  );
});

// eslint-disable-next-line react/display-name
export const Slide = forwardRef(({ children }, ref) => {
  return (
    <li className="glide__slide" ref={ref}>
      {children}
    </li>
  );
});

export default {
  Carousel,
  Slide,
};
